// Activity log — human-readable Who / Tenant / What happened / When columns.

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Icon } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import {
  auditActorLabel,
  auditCodeTone,
  formatAuditSummary,
  humanizeAuditCode,
  isDashboardNoise,
} from '../lib/auditLabels.js';
import { LoadingState } from '../components/LoadingState.js';
import { useIsCompact } from '../lib/useIsCompact.js';
import { resourceTypeLabel } from '../lib/humanLabels.js';

interface AuditRow {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  actor_user_id: string | null;
  actor_name?: string | null;
  tenant_name?: string | null;
  created_at: string;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface Column {
  key: string;
  header: string;
  width?: string;
  render: (r: AuditRow) => ReactNode;
}

const COLUMNS: Column[] = [
  {
    key: 'who',
    header: 'Who',
    width: '22%',
    render: (r) => (
      <span className="text-on-surface" data-testid={`activity-actor-${r.id}`}>
        {auditActorLabel(r)}
      </span>
    ),
  },
  {
    key: 'tenant',
    header: 'Tenant',
    width: '18%',
    render: (r) => (
      <span className="text-on-surface-variant">
        {r.tenant_name?.trim() || '—'}
      </span>
    ),
  },
  {
    key: 'action',
    header: 'What happened',
    width: '34%',
    render: (r) => {
      const tone = auditCodeTone(r.action);
      const cls =
        tone === 'quiet'
          ? 'text-on-surface-variant'
          : tone === 'warning'
            ? 'text-error'
            : 'text-primary';
      // UX-025: the row carried role="link" and an onClick, which overrides
      // its table-row semantics and gives none of a link's affordances. The
      // identity cell is a real anchor instead.
      return (
        <Link
          to={`/activity/${r.id}`}
          className={`rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${cls}`}
          title={formatAuditSummary(r)}
          data-testid={`activity-row-link-${r.id}`}
        >
          {humanizeAuditCode(r.action)}
        </Link>
      );
    },
  },
  {
    key: 'created_at',
    header: 'When',
    width: '16%',
    render: (r) => (
      <span title={new Date(r.created_at).toLocaleString()}>
        {relativeTime(r.created_at)}
      </span>
    ),
  },
];

export function ActivityLogPage() {
  const [auditRows, setAuditRows] = useState<AuditRow[] | null>(null);
  const [showServiceRows, setShowServiceRows] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCompact = useIsCompact();
  const [searchParams, setSearchParams] = useSearchParams();

  // UX-014: pages across the product link here with resource_type/resource_id
  // (door detail's "View events", the related rail) expecting a scoped log.
  // Nothing read them, so every one of those links landed on the unfiltered
  // list. GET /api/v1/audit supports all three server-side.
  const resourceType = searchParams.get('resource_type')?.trim() ?? '';
  const resourceId = searchParams.get('resource_id')?.trim() ?? '';
  const action = searchParams.get('action')?.trim() ?? '';
  const hasFilter = Boolean(resourceType || resourceId || action);

  useEffect(() => {
    let cancelled = false;
    setAuditRows(null);
    setError(null);
    const query = new URLSearchParams({ limit: '200' });
    if (resourceType) query.set('resource_type', resourceType);
    if (resourceId) query.set('resource_id', resourceId);
    if (action) query.set('action', action);
    apiClient
      .get<{ data: AuditRow[] }>(`/api/v1/audit?${query.toString()}`)
      .then((res) => {
        if (!cancelled) setAuditRows(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, [resourceType, resourceId, action]);

  // UX-035: the audit log filtered `isDashboardNoise` rows out of itself and
  // said nothing about it, so an operator reading this page as a record was
  // reading a subset without knowing. An audit log that silently omits rows is
  // hard to defend to whoever eventually needs it.
  //
  // Service rows stay hidden by default — they are genuine noise — but the
  // page now says how many and offers them.
  const allRows = auditRows ?? [];
  const serviceRows = allRows.filter((r) => isDashboardNoise(r.action));
  const visibleRows = showServiceRows ? allRows : allRows.filter((r) => !isDashboardNoise(r.action));

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="receipt_long" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">Activity log</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Who did what, and for which tenant.
            </p>
          </div>
        </div>
      </header>

      {auditRows !== null ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-body-md text-body-md text-on-surface-variant">
          <span>
            {/* A filtered count is not a total, and the page used to show
                neither. */}
            {visibleRows.length === allRows.length
              ? `${allRows.length} ${allRows.length === 1 ? 'entry' : 'entries'}`
              : `${visibleRows.length} of ${allRows.length} entries shown`}
          </span>
          {serviceRows.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowServiceRows((v) => !v)}
              aria-pressed={showServiceRows}
              className="rounded-sm text-primary underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {showServiceRows
                ? `Hide ${serviceRows.length} service ${serviceRows.length === 1 ? 'entry' : 'entries'}`
                : `Show ${serviceRows.length} service ${serviceRows.length === 1 ? 'entry' : 'entries'}`}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* An applied filter has to be visible and reversible — otherwise a
          deep link silently shows a subset and the operator reads it as the
          whole log. */}
      {hasFilter ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-container px-4 py-3 font-body-md text-body-md text-on-surface-variant"
        >
          <Icon name="filter_alt" className="text-on-surface-variant" />
          <span>
            Filtered to{' '}
            <strong className="text-on-surface">
              {[
                resourceType ? resourceTypeLabel(resourceType) : null,
                resourceId ? `#${resourceId.slice(0, 8)}` : null,
                action ? humanizeAuditCode(action) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </strong>
          </span>
          <button
            type="button"
            onClick={() => setSearchParams({}, { replace: true })}
            className="rounded-sm text-primary underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Show all activity
          </button>
        </div>
      ) : null}

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div className="p-6 text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : auditRows == null ? (
          <LoadingState />
        ) : visibleRows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            {hasFilter
              ? 'No portal activity matches this filter.'
              : 'No portal activity recorded yet.'}
          </div>
        ) : isCompact ? (
          <ul className="divide-y divide-outline-variant">
            {visibleRows.map((row) => {
              const [primary, ...rest] = COLUMNS;
              return (
                <li key={row.id}>
                  <Link
                    to={`/activity/${row.id}`}
                    className="block space-y-2 p-4 hover:bg-surface-container-low"
                    data-testid={`activity-row-${row.id}`}
                  >
                    {primary ? (
                      <div className="font-title-sm text-title-sm text-on-surface">
                        {primary.render(row)}
                      </div>
                    ) : null}
                    <dl className="space-y-1 font-body-md text-body-md">
                      {rest.map((c) => (
                        <div key={c.key} className="flex justify-between gap-3">
                          <dt className="shrink-0 text-on-surface-variant">{c.header}</dt>
                          <dd className="min-w-0 break-words text-right text-on-surface">
                            {c.render(row)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </Link>
                  </li>
                );
              })}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
                <thead className="bg-surface-container">
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3"
                        style={c.width ? { width: c.width } : undefined}
                      >
                        {c.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {visibleRows.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-surface-container-low transition-colors"
                      data-testid={`activity-row-${row.id}`}
                    >
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          className="px-4 py-3 font-body-md text-body-md text-on-surface"
                        >
                          {c.render(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
        )}
      </section>
    </div>
  );
}
